import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
  SimpleChanges
} from "@angular/core";
import { AsyncPipe, CommonModule, JsonPipe } from "@angular/common";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { FlexModule } from "@angular/flex-layout/flex";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MessageBoxComponent } from "../../message-box/message-box.component";
import { MatCardModule } from "@angular/material/card";
import {
  AppCalculator,
  Application,
  CheckActiveOverdraftAccountDetails,
  GetRateCardDetailsFn,
  LoanTermValue,
  LoanTermValueOptions,
  PaymentFrequencyValue,
  PaymentFrequencyValueOptions,
  RatecardDetails,
  SaveApplicationPricingDetailsData,
  SliderComponentValue,
  UpdateApplicationData,
  UpdateApplicationFn,
  UpdateApplicationSfFn,
  YesNoValue,
  booleanToYesNo,
  getDocFee,
  getInterestRate,
  getRbaRate,
  isAdminOrCreditUserOrSalesAM,
  isInternalUser,
  OverdraftPaymentBreakupDialogData,
  calculateBusinessOverdraftFees,
  LoanTermType,
  GetContractStatusFn,
  getBrokerage, isAdminOrCreditUser, isAdmin, isAdminOrCreditOrOperationsOrSalesUser,
  isCreditUser,
  MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT,
  getBrokerageManualOverride,
  CheckActiveOverdraftAccountFn,
  getAbn,
  isBusinessOverdraftVariationPurpose,
  BUSINESS_OVERDRAFT_MAX_BROKERAGE_PERCENT,
} from "@portal-workspace/grow-shared-library";
import {Subject, Subscription, catchError, of, tap} from "rxjs";
import { DisableControlDirective } from "../../../directives/disable-control.directive";
import { InputMaskModule } from "@ngneat/input-mask";
import { YesNoComponent } from "../../yes-no-component/yes-no.component";
import { MatTableModule } from "@angular/material/table";
import { PercentageInputComponent } from "../../percentage-input-component/percentage-input.component";
import { PaymentFrequencySelectionComponent } from "../../payment-frequency-selection-component/payment-frequency-selection.component";
import { CurrencyInputComponent } from "../../currency-selection-component/currency-input.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { ApplicationDialogService } from "../../application-dialog-component/application-dialog.service";
import { PortalHotToastService } from "../../portal-hot-toast-component/hot-toast.service";
import { loadingFor } from "@ngneat/loadoff";
import {
  applicationToAdverseOnFile,
  applicationToBrokerageAmount,
  applicationToCrediRateAdjustment,
  applicationToEquifaxDirectorScoreMeetsThreshold,
  applicationToGstAgeMonths,
  applicationToGstAgeMonthsWithContinuity,
  applicationToLastUpdatedByUser,
  applicationToLoanAmount,
  applicationToLoanTerms,
  applicationToMargin,
  applicationToPaymentFrequency,
  applicationToPropertyOwner,
  applicationToTermRenewal,
  applicationToEarlyTerminationFee,
  applicationToAdditionalCommission,
  getUser
} from "../../component-utils";
import { Moment } from "moment";
import moment from "moment";
import _ from "lodash";
import {LooseCurrencyPipe} from '../../../pipes/loose-currency.pipe';
import {PercentagePipe} from '../../../pipes/percentage.pipe';
import {MarkDirective} from '../../../directives/mark-as-dirty.directive';

export type BusinessOverdraftPricingComponentEvent = BusinessOverdraftPricingComponentSavedEvent;

export interface BusinessOverdraftPricingComponentSavedEvent {
  type: 'business-overdraft-pricing-saved',
  rst: OverdraftPaymentBreakupDialogData
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'business-overdraft-pricing',
  templateUrl: './business-overdraft-pricing.component.html',
  styleUrls: ['./business-overdraft-pricing.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MatButtonModule,
    MatFormFieldModule,
    AsyncPipe,
    JsonPipe,
    MatCardModule,
    MessageBoxComponent,
    DisableControlDirective,
    InputMaskModule,
    YesNoComponent,
    MatTableModule,
    PercentageInputComponent,
    PaymentFrequencySelectionComponent,
    CurrencyInputComponent,
    LooseCurrencyPipe,
    PercentagePipe,
    MarkDirective,
  ],
})
export class BusinessOverdraftPricingComponent implements OnInit, OnChanges {

  markSubject = new Subject<boolean>();

  @Input({ required: true }) getRateCardDetailsFn!: GetRateCardDetailsFn;
  @Input({ required: true }) updateApplicationFn!: UpdateApplicationFn;
  @Input({ required: true }) updateApplicationSfFn!: UpdateApplicationSfFn;
  @Input({required: true}) getContractStatusFn!: GetContractStatusFn;
  @Input({ required: true }) application!: Application;
  @Input({ required: true }) currentRstValue!: OverdraftPaymentBreakupDialogData;
  @Input({ required: false }) activeAccountDetails: CheckActiveOverdraftAccountDetails | null = null;
  @Input({ required: false }) checkActiveOverdraftAccountFn: CheckActiveOverdraftAccountFn | null = null;
  @Output() events = new EventEmitter<BusinessOverdraftPricingComponentEvent>();
  @ViewChild('readMode') readModeTemplate!: TemplateRef<any>;
  @ViewChild('editMode') editModeTemplate!: TemplateRef<any>;

  subscriptions: Subscription[] = [];
  /** Local copy so we can fill from parent input OR a self-fetch when parent never loaded it. */
  private resolvedActiveAccountDetails: CheckActiveOverdraftAccountDetails | null = null;
  private activeAccountFetchStarted = false;

  // Captured once, from the saved application at its saved inputs: did the stored brokerage
  // equal what the rate card calculates? When true (and not a manual override) the brokerage
  // tracks the calculation as the operator edits the limit / facility establishment fee — the
  // brokerage % is preserved and the $ is recalculated — instead of freezing the saved $ and
  // surfacing a spurious drift banner. `null` = not yet determined.
  private savedBrokerageMatchedCalculation: boolean | null = null;

  formBuilder: FormBuilder = inject(FormBuilder);
  applicationDialogService: ApplicationDialogService = inject(ApplicationDialogService);
  toastService: PortalHotToastService = inject(PortalHotToastService);

  loader = loadingFor('saveRate');

  user = getUser();
  isInternalUser = isInternalUser(this.user)
  isAdminOrCreditUserOrSalesAM = isAdminOrCreditUserOrSalesAM(this.user);
  canEditPricingByRole = isAdminOrCreditOrOperationsOrSalesUser(this.user);
  isNotAdminOrCreditUser = !isAdminOrCreditUser(this.user);
  isAdmin = isAdmin(this.user);
  isCreditUser = isCreditUser(this.user);

  isContractPending: boolean = true;
  isApplicationStagePostQa = true;
  isLimitChangeOrRenewal = false;

  dataSource: (
    {name: string, highlight?: (value: string | null | undefined) => boolean, type: 'text', value: string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'currency', value: number | string | null | undefined} |
    {name: string, highlight?: (value: number | string | null | undefined) => boolean, type: 'percentage', value: number | string | null | undefined}
  )[] = [];
  currentTemplate: TemplateRef<any> | null = null;

  step2MonthlyRepayment: string = '';
  step2EstimatedDrawdownDate: Moment = moment();
  MIN_LIMIT = 0.01
  step2MinLimit: number = this.MIN_LIMIT;
  step2MaxLimit: number | null = null;
  isDisable = true;

  // credit adjustment
  ADMIN_MIN_CREDIT_RATE = -10000;
  ADMIN_MAX_CREDIT_RATE = 10000;
  CREDIT_MIN_CREDIT_RATE = -5;
  CREDIT_MAX_CREDIT_RATE = 5;
  NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE = -1;
  NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE = 5;
  MIN_CREDIT_RATE = this.ADMIN_MIN_CREDIT_RATE;
  MAX_CREDIT_RATE = this.ADMIN_MAX_CREDIT_RATE;

  rateCard: RatecardDetails | null = null;
  calculator: AppCalculator | null = null;
  rst: OverdraftPaymentBreakupDialogData | null = null;
  data: Partial<UpdateApplicationData> | null = null;
  brokerageDollar?: number;
  interestRate?: number;
  rbaRate?: number;
  docFee?: number;
  brokerage?: number;
  calculatedBrokeragePercent?: number;
  calculatedBrokerageDollar?: number;
  manualBrokerageDollar?: number;
  manualBrokeragePercent?: number;
  monthlyFacilityFeePercentage?: number;
  lastEditedByUser ?: string | null

  // True when the displayed brokerage differs from what today's rate card would calculate.
  // Surfaces a banner so the operator can decide whether to keep the displayed amount
  // (click the pencil) or regenerate a quote to pick up the current calculation.
  // `brokerageMatchesCalculation` already returns true until the calculated figure is
  // populated (which only happens once the rate card has loaded), so no explicit guard
  // on `rateCard` is needed here.
  get legacyBrokerageDrift(): boolean {
    return !this.brokerageMatchesCalculation;
  }

  formControlStep2LoanTerms!: FormControl<LoanTermValue>;
  formControlStep2PaymentFrequency!: FormControl<PaymentFrequencyValue>;
  formControlStep2PropertyOwner!: FormControl<YesNoValue>;
  formControlStep2AdverseOnFile!: FormControl<YesNoValue>;
  formControlStep2EquifaxDirectorScoreAbove700!: FormControl<YesNoValue>;
  formControlStep2LoanAmount!: FormControl<SliderComponentValue>;
  formControlBrokerageManualOverride!: FormControl<boolean | null>;
  formControlBrokerageDisplay!: FormControl<number | null>;
  formControlCreditRateAdjustment!: FormControl<number | null>;
  formControlAdditionalCommission!: FormControl<YesNoValue>;


  formGroupStep2!: FormGroup<{
    loanAmount: FormControl<SliderComponentValue>,
    loanTerms: FormControl<LoanTermValue>,
    paymentFrequency: FormControl<PaymentFrequencyValue>,
    propertyOwner: FormControl<YesNoValue>,
    adverseOnFile: FormControl<YesNoValue>,
    equifaxScore: FormControl<YesNoValue>,
    creditRateAdjustment: FormControl<number | null>,
    additionalCommission: FormControl<YesNoValue>,
  }>;

  ngOnInit() {
    this.formControlStep2LoanAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlStep2LoanTerms = this.formBuilder.control(LoanTermValueOptions[2], [Validators.required]);
    this.formControlStep2PaymentFrequency = this.formBuilder.control(PaymentFrequencyValueOptions[2], [Validators.required]);
    this.formControlStep2PropertyOwner = this.formBuilder.control(true, [Validators.required]);
    this.formControlStep2AdverseOnFile = this.formBuilder.control(false, [Validators.required]);
    this.formControlStep2EquifaxDirectorScoreAbove700 = this.formBuilder.control(true, [Validators.required]);
    this.formControlBrokerageManualOverride = this.formBuilder.control(false);
    this.formControlBrokerageDisplay = this.formBuilder.control(null);
    this.formControlCreditRateAdjustment = this.formBuilder.control(0.0, [Validators.required]);
    this.formControlAdditionalCommission = this.formBuilder.control(false, [Validators.required]);

    this.formGroupStep2 = this.formBuilder.group({
      loanAmount: this.formControlStep2LoanAmount,
      loanTerms: this.formControlStep2LoanTerms,
      paymentFrequency: this.formControlStep2PaymentFrequency,
      propertyOwner: this.formControlStep2PropertyOwner,
      adverseOnFile: this.formControlStep2AdverseOnFile,
      equifaxScore: this.formControlStep2EquifaxDirectorScoreAbove700,
      creditRateAdjustment: this.formControlCreditRateAdjustment,
      additionalCommission: this.formControlAdditionalCommission,
    });

    this.populate();

    const loanPurposeName = this.application?.AppInfo?.LoanPurpose?.name;
    this.isLimitChangeOrRenewal = ['Limit Increase', 'Limit Decrease', 'Renewal'].includes(loanPurposeName ?? '');

    // AdditionalCommission is enabled for new overdraft + limit increase, disabled
    // for limit decrease and renewal-with-no-limit-change. Renewal with an actual
    // limit change is treated as a limit change (enabled). Re-applied on loanAmount
    // changes and when activeAccountDetails arrives async (see ngOnChanges below).
    this.applyAdditionalCommissionGate();
    this.subscriptions.push(this.formControlStep2LoanAmount.valueChanges.pipe(
      tap(() => this.applyAdditionalCommissionGate())
    ).subscribe());

    this.createReadonlyTableDataSource();

    if (this.isNotAdminOrCreditUser) {
      this.formControlStep2PropertyOwner.disable();
      this.formControlStep2AdverseOnFile.disable();
      this.formControlStep2EquifaxDirectorScoreAbove700.disable();

      const loanAmount = applicationToLoanAmount(this.application) ?? 0;
      const adjustmentToLoanAmount = (loanAmount * 0.1);
      const maxLoanAmount = _.round(loanAmount + adjustmentToLoanAmount);
      this.step2MaxLimit = maxLoanAmount;

    }

    // Recompute the system-calculated brokerage whenever an input that affects it changes.
    // We keep this in sync so the drift banner and override reference label stay accurate;
    // the editable display itself continues to show the saved/manual value.
    // AdditionalCommission is included because toggling it applies the +1% brokerage
    // uplift in calculateBusinessOverdraftFees — without it, the brokerage $/% would
    // not follow the calculation until a quote is regenerated. The AC gate mutates the
    // control with { emitEvent: false }, so only genuine user toggles trigger this.
    const previewTriggers: FormControl<any>[] = [
      this.formControlStep2LoanAmount,
      this.formControlStep2PropertyOwner,
      this.formControlStep2AdverseOnFile,
      this.formControlStep2EquifaxDirectorScoreAbove700,
      this.formControlCreditRateAdjustment,
      this.formControlAdditionalCommission,
    ];
    previewTriggers.forEach(control => {
      this.subscriptions.push(control.valueChanges.pipe(tap(() => this.refreshCalculatedBrokeragePreview())).subscribe());
    });

    this.subscriptions.push(this.getRateCardDetailsFn(this.application.UserId, 'BusinessOverdraft').pipe(
      tap(rateCardDetails => {
        this.rateCard = rateCardDetails;
        this.refreshCalculatedBrokeragePreview();
        this.applyManualBrokerageState();
      })
    ).subscribe());

    // Variation brokerage needs currentLimit from the active OD account. Parent may not have
    // loaded it yet (or at all if ABN resolution failed earlier) — fetch here as a fallback.
    this.resolvedActiveAccountDetails = this.activeAccountDetails;
    this.ensureActiveAccountDetailsLoaded();

    this.subscriptions.push(this.getContractStatusFn(this.application.ApplicationId).pipe(
      tap(r => {
        const {contractPending} = r.signers.reduce((acc, signer) => {
          const contractPending = ["created", "sent", "delivered", "signed", "completed", "faxpending"].includes(signer.status);
          acc.contractPending = acc.contractPending || contractPending;
          return acc;
        }, { contractPending: false});
        this.isContractPending = contractPending;
      })
    ).subscribe());

    this.isApplicationStagePostQa = ['QA', 'Settlement', 'Closed Lost', 'Closed Lost'].includes(this.application.InteflowStatusStageName ?? '');
  }

  populate() {
    this.formControlStep2LoanTerms.setValue(applicationToLoanTerms(this.application));
    this.formControlStep2LoanAmount.setValue(applicationToLoanAmount(this.application));
    this.formControlStep2PaymentFrequency.setValue(applicationToPaymentFrequency(this.application) ??
      PaymentFrequencyValueOptions.find(o => o.type === 'Monthly') ?? null);
    this.formControlStep2PropertyOwner.setValue(applicationToPropertyOwner(this.application));
    this.formControlStep2AdverseOnFile.setValue(applicationToAdverseOnFile(this.application));
    this.formControlStep2EquifaxDirectorScoreAbove700.setValue(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=700'));
    this.formControlCreditRateAdjustment.setValue(applicationToCrediRateAdjustment(this.application));
    this.formControlAdditionalCommission.setValue(applicationToAdditionalCommission(this.application));

    this.brokerageDollar = applicationToBrokerageAmount(this.application) ?? 0;
    this.brokerage = getBrokerage(this.application);
    this.interestRate = getInterestRate(this.application) as number;
    this.rbaRate = getRbaRate(this.application);
    this.docFee = getDocFee(this.application) as number;

    // Re-apply the AC gate after populate so the disable rule (and the value
    // reset that comes with it) wins over whatever was just set from the
    // persisted application. Without this, switchMode() → populate() would
    // overwrite the gated `false` with the persisted `Yes` and re-leak the
    // +1.5% uplift into margin on the next quote.
    this.applyAdditionalCommissionGate();

    this.applyManualBrokerageState();
    this.syncBrokerageDisplay();
  }

  /**
   * Sets the manual-override flag + manual brokerage values from the saved application.
   * The `legacyBrokerageDrift` banner state is derived live via the getter, so no
   * imperative recomputation is needed here.
   */
  private applyManualBrokerageState(): void {
    const explicitOverride = getBrokerageManualOverride(this.application);
    if (explicitOverride) {
      this.formControlBrokerageManualOverride.setValue(true, { emitEvent: false });
      this.manualBrokerageDollar = applicationToBrokerageAmount(this.application) ?? 0;
      this.manualBrokeragePercent = getBrokerage(this.application);
    } else {
      this.formControlBrokerageManualOverride.setValue(false, { emitEvent: false });
      this.manualBrokerageDollar = undefined;
      this.manualBrokeragePercent = undefined;
    }

    this.syncBrokerageDisplay();
  }

  /** True when the calculated brokerage materially differs from the manual override. */
  get showCalculatedBrokerageReference(): boolean {
    if (this.calculatedBrokerageDollar == null) return false;
    const overrideDollar = this.manualBrokerageDollar ?? this.brokerageDollar;
    if (overrideDollar == null) return false;
    return Math.abs(this.calculatedBrokerageDollar - overrideDollar) > 0.01;
  }

  // Mirrors asset-pricing's matchBrokerageAmount: `=` when displayed brokerage equals
  // what the rate card would calculate, `~` when they diverge (manual override or
  // legacy drift). Falls back to `=` until a calculation is available.
  get brokerageMatchesCalculation(): boolean {
    if (this.calculatedBrokerageDollar == null) return true;
    const displayed = this.displayedBrokerageDollar;
    if (displayed == null) return true;
    return Math.abs(this.calculatedBrokerageDollar - displayed) < 0.01;
  }

  createReadonlyTableDataSource() {
    this.lastEditedByUser = applicationToLastUpdatedByUser(this.application)?.name

    const baseDataSource: typeof this.dataSource = [
      { name: 'Facility Limit', type: 'currency', value: applicationToLoanAmount(this.application) },
      { name: 'Loan Terms', type: 'text', value: `${applicationToLoanTerms(this.application)?.type ?? ''} months` },
      { name: 'RBA Rate (%)', type: 'percentage', value: getRbaRate(this.application) },
      { name: 'Monthly Facility Fees ($)', type: 'currency', value: this.currentRstValue?.term.monthlyFacilityFee},
      { name: 'Commission (ex GST)', type: 'currency', value: applicationToBrokerageAmount(this.application) },
      ...(getBrokerageManualOverride(this.application)
        ? [{ name: 'Brokerage manually overridden', type: 'text' as const, value: 'Yes' }]
        : []),
      { name: 'Property Owner', type: 'text', value: booleanToYesNo(applicationToPropertyOwner(this.application)) },
      { name: 'Adverse on file', type: 'text', value: booleanToYesNo(applicationToAdverseOnFile(this.application)) },
      { name: 'Equifax Director Score ≥ 700', type: 'text', value: booleanToYesNo(applicationToEquifaxDirectorScoreMeetsThreshold(this.application, '>=700')) },

      { name: 'Doc/Annual Fee ($)', type: 'currency', value: getDocFee(this.application) },
      { name: 'Margin (%)', type: 'percentage', value: applicationToMargin(this.application) },
      { name: 'Interest Rate (%)', type: 'percentage', value: getInterestRate(this.application) },
      { highlight: (v: any) => Number(v) != 0, name: 'Credit Rate Adjustment', type: 'percentage', value: applicationToCrediRateAdjustment(this.application) },
      { name: 'Additional Commission', type: 'text', value: booleanToYesNo(applicationToAdditionalCommission(this.application)) },
    ];

    const termRenewal = applicationToTermRenewal(this.application);
    if (termRenewal) {
      baseDataSource.push({ name: 'Term Renewal', type: 'text', value: booleanToYesNo(termRenewal) });
    }

    const earlyTerminationFee = applicationToEarlyTerminationFee(this.application);
    if (earlyTerminationFee !== null && earlyTerminationFee !== undefined) {
      baseDataSource.push({ name: 'Early Termination Fee ($)', type: 'currency', value: earlyTerminationFee });
    }

    this.dataSource = baseDataSource;
  }

  ngOnChanges(changes: SimpleChanges) {
    const applicationChange = changes['application'];
    const currentRstValueChange = changes['currentRstValue'];
    const activeAccountDetailsChange = changes['activeAccountDetails'];
    if (applicationChange || currentRstValueChange) {
      this.createReadonlyTableDataSource();
    }

    // Variation brokerage (limit-increase delta vs full limit) needs both the rate card
    // and activeAccountDetails. The rate-card subscription often resolves first, so the
    // initial preview still uses full-limit brokerage. Recompute when the active account
    // arrives (or when TermRenewal / loan purpose changes on the application) so the
    // drift banner can surface the mismatch (e.g. $2,400 saved vs $400 calculated).
    if (activeAccountDetailsChange && this.activeAccountDetails) {
      this.resolvedActiveAccountDetails = this.activeAccountDetails;
    }
    if (applicationChange) {
      this.activeAccountFetchStarted = false;
      this.ensureActiveAccountDetailsLoaded();
    }
    if (applicationChange || activeAccountDetailsChange) {
      this.refreshCalculatedBrokeragePreview();
    }

    // activeAccountDetails is loaded async by the parent; once it arrives the
    // pure-renewal vs. limit-change-renewal verdict can flip, so re-run the gate.
    if (activeAccountDetailsChange || applicationChange) {
      // Guard against the very first change pass before formGroupStep2 exists
      // (ngOnChanges runs before ngOnInit on the initial pass).
      if (this.formControlAdditionalCommission) {
        this.applyAdditionalCommissionGate();
      }
    }
  }

  /**
   * For Limit Increase / Decrease / Renewal, load active-account currentLimit if missing.
   * Without it, calculateBusinessOverdraftVariationFees is a no-op and brokerage stays on
   * the full facility limit — so the drift banner never fires after Renewal flips to No.
   */
  private ensureActiveAccountDetailsLoaded(): void {
    if (this.resolvedActiveAccountDetails?.currentLimit != null) {
      return;
    }
    if (this.activeAccountFetchStarted || !this.checkActiveOverdraftAccountFn) {
      return;
    }
    const loanPurpose = this.application?.AppInfo?.LoanPurpose ?? null;
    if (!isBusinessOverdraftVariationPurpose(loanPurpose)) {
      return;
    }
    const abn = getAbn(this.application);
    if (!abn) {
      return;
    }
    this.activeAccountFetchStarted = true;
    this.subscriptions.push(
      this.checkActiveOverdraftAccountFn(abn).pipe(
        tap(response => {
          if (response?.status && response.payload) {
            this.resolvedActiveAccountDetails = response.payload;
            this.refreshCalculatedBrokeragePreview();
          }
        }),
        catchError(() => of(null)),
      ).subscribe(),
    );
  }

  ngAfterViewInit() {
    this.currentTemplate = this.readModeTemplate;
  }

  switchMode() {
    if (this.currentTemplate == this.editModeTemplate) {
      this.currentTemplate = this.readModeTemplate;
    } else {
      this.populate();
      this.currentTemplate = this.editModeTemplate;
    }
  }

  // Disabled when there is no incremental limit being added to the facility:
  //   - Limit Decrease  → no upside to commission
  //   - Renewal where requested limit equals current limit → pure rollover
  // For Renewal with an actual limit change, fall through to enabled.
  //
  // Reads the requested limit from the FORM control (not the persisted app)
  // so the gate reacts to live edits before quote generation. When
  // activeAccountDetails has not yet loaded, conservatively disable for
  // Renewal — ngOnChanges re-runs the gate once the input arrives.
  private shouldDisableAdditionalCommission(loanPurposeName: string | undefined): boolean {
    if (loanPurposeName === 'Limit Decrease') {
      return true;
    }
    if (loanPurposeName === 'Renewal') {
      if (this.activeAccountDetails == null) {
        return true;
      }
      const requestedLimit = this.formControlStep2LoanAmount?.value
        ?? applicationToLoanAmount(this.application)
        ?? 0;
      const currentLimit = this.activeAccountDetails.currentLimit ?? 0;
      return requestedLimit === currentLimit;
    }
    return false;
  }

  // Single source of truth for the AC enable/disable + value-reset.
  // CRITICAL: forces value to false when disabling, otherwise the disabled
  // FormControl still reports the previously-set value via `.value`, which
  // would let calculateEstimation() apply the +1.5% rate uplift on a Limit
  // Decrease / pure-renewal app and silently inflate margin.
  private applyAdditionalCommissionGate() {
    const loanPurposeName = this.application?.AppInfo?.LoanPurpose?.name;
    if (this.shouldDisableAdditionalCommission(loanPurposeName)) {
      if (this.formControlAdditionalCommission.value !== false) {
        this.formControlAdditionalCommission.setValue(false, {emitEvent: false});
      }
      if (this.formControlAdditionalCommission.enabled) {
        this.formControlAdditionalCommission.disable({emitEvent: false});
      }
    } else {
      if (this.formControlAdditionalCommission.disabled) {
        this.formControlAdditionalCommission.enable({emitEvent: false});
      }
    }
  }

  isBrokerageManualOverrideActive(): boolean {
    // Form control is seeded from the saved application in applyManualBrokerageState() for
    // every role, and only privileged users can mutate it via the pencil dialog, so the
    // control value is the single source of truth here regardless of role.
    return this.formControlBrokerageManualOverride.value ?? false;
  }

  refreshCalculatedBrokeragePreview(): void {
    if (!this.rateCard) {
      return;
    }
    const preview = this.buildCalculatedEstimation();
    this.calculatedBrokerageDollar = preview.term.brokerageDollar;
    this.calculatedBrokeragePercent = preview.term.brokerage;
    this.captureSavedBrokerageBaseline();
    // When the saved brokerage originally matched the system calculation and it is not a
    // manual override, keep it in step with the calculation so editing the limit / facility
    // establishment fee preserves the brokerage % and recalculates the brokerage $.
    if (this.savedBrokerageMatchedCalculation === true && !this.isBrokerageManualOverrideActive()) {
      this.brokerageDollar = this.calculatedBrokerageDollar;
      this.brokerage = this.calculatedBrokeragePercent;
    }
    this.syncBrokerageDisplay();
  }

  /**
   * Decide once — from the saved application at its saved inputs — whether the stored brokerage
   * matched the rate-card calculation. Deferred for variation purposes until the active account
   * details load, since the variation calculation depends on the current limit.
   */
  private captureSavedBrokerageBaseline(): void {
    if (this.savedBrokerageMatchedCalculation !== null) {
      return;
    }
    if (this.isBrokerageManualOverrideActive()) {
      this.savedBrokerageMatchedCalculation = false;
      return;
    }
    const loanPurpose = this.application?.AppInfo?.LoanPurpose ?? null;
    if (isBusinessOverdraftVariationPurpose(loanPurpose) && this.resolvedActiveAccountDetails?.currentLimit == null) {
      return;
    }
    const savedBrokerageDollar = applicationToBrokerageAmount(this.application);
    if (savedBrokerageDollar == null || this.calculatedBrokerageDollar == null) {
      return;
    }
    this.savedBrokerageMatchedCalculation =
      Math.abs(this.calculatedBrokerageDollar - savedBrokerageDollar) < 0.01;
  }

  /** Mirror the current effective brokerage into the read-only display form control. */
  private syncBrokerageDisplay(): void {
    this.formControlBrokerageDisplay.setValue(this.displayedBrokerageDollar ?? null, { emitEvent: false });
  }

  // Prefer the saved / manual amount so edit mode matches view mode. The live rate-card
  // calculation is surfaced via the drift banner and system-calculation reference; it is
  // only persisted when the operator accepts a regenerated quote (or confirms via pencil).
  get displayedBrokerageDollar(): number | undefined {
    if (this.isBrokerageManualOverrideActive()) {
      return this.manualBrokerageDollar ?? this.brokerageDollar;
    }
    return this.brokerageDollar ?? this.calculatedBrokerageDollar;
  }

  get displayedBrokeragePercent(): number | undefined {
    // Always derive % from the $ we show so the two cannot disagree — including
    // under manual override, where the saved Brokerage % may be stale relative to
    // the saved $ (e.g. $2,000 with a leftover 4% from a different conversion base).
    const dollar = this.displayedBrokerageDollar;
    const base = this.getBrokerageConversionBaseAmount();
    if (dollar != null && base > 0) {
      return _.round((dollar * 100) / base, 2);
    }
    if (this.isBrokerageManualOverrideActive() && this.manualBrokeragePercent != null) {
      return this.manualBrokeragePercent;
    }
    return this.brokerage ?? this.calculatedBrokeragePercent;
  }

  /**
   * $ ↔ % conversion base for the brokerage read-out / edit dialog.
   * Prefer the base implied by the live calculation (handles limit-increase delta
   * vs full facility limit); fall back to the requested facility limit.
   */
  private getBrokerageConversionBaseAmount(): number {
    if (
      this.calculatedBrokerageDollar != null
      && this.calculatedBrokeragePercent != null
      && this.calculatedBrokeragePercent > 0
    ) {
      return _.round(
        (this.calculatedBrokerageDollar * 100) / this.calculatedBrokeragePercent,
        2,
      );
    }
    return this.formControlStep2LoanAmount?.value
      ?? applicationToLoanAmount(this.application)
      ?? 0;
  }

  private buildCalculatedEstimation() {
    const date = moment().add(1, 'day');
    const loanTermValue: LoanTermValue = this.formControlStep2LoanTerms.value;
    const loanTermType: LoanTermType | null = loanTermValue?.type ?? null;
    const propertyOwner = this.formControlStep2PropertyOwner.value ?? false;
    const adverseOnFile = this.formControlStep2AdverseOnFile.value ?? false;
    const equifaxDirectorScoreAbove700 = this.formControlStep2EquifaxDirectorScoreAbove700.value ?? false;
    const gstAgeMonths = applicationToGstAgeMonths(this.application);
    const gstAgeMonthsWithContinuity = applicationToGstAgeMonthsWithContinuity(this.application);
    const facilityLimit = this.formControlStep2LoanAmount.value ?? 0;
    const creditRateAdjustment = this.formControlCreditRateAdjustment.value ?? 0;
    const additionalCommission = this.formControlAdditionalCommission.value ?? false;

    const loanPurpose = this.application?.AppInfo?.LoanPurpose ?? null;
    const termRenewal = applicationToTermRenewal(this.application);

    return calculateBusinessOverdraftFees(date, {
      type: 'BusinessOverdraft',
      loanTermType,
      propertyOwner,
      adverseOnFile,
      equifaxDirectorScoreAbove700,
      gstAgeMonths,
      gstAgeMonthsWithContinuity,
      rateCard: this.rateCard,
      facilityLimit,
      creditRateAdjustment,
      additionalCommission,
      loanPurpose,
      termRenewal,
      activeAccountDetails: this.resolvedActiveAccountDetails ?? this.activeAccountDetails,
    });
  }

  onEditManualBrokerage(): void {
    if (!this.canEditPricingByRole) {
      return;
    }
    // Pre-fill with what's currently shown next to the pencil (saved amount or active manual
    // override), not the system-calculated value — otherwise the dialog contradicts the
    // legacy-drift banner that asks the operator to preserve the saved amount.
    const initialAmount = this.displayedBrokerageDollar ?? 0;
    // Same base as the on-screen % (inferred from the live calculation when available).
    const loanAmount = this.getBrokerageConversionBaseAmount()
      || (this.formControlStep2LoanAmount.value ?? 0);

    this.subscriptions.push(
      this.applicationDialogService.openEditBrokerageDailog({
        brokerageAmount: initialAmount,
        loanAmount,
        maxBrokeragePercentage: BUSINESS_OVERDRAFT_MAX_BROKERAGE_PERCENT,
        showOverrideHelperText: true,
      }).afterClosed().subscribe(result => {
        // Cancel/dismiss → no-op. Avoid touching form controls so the displayed
        // brokerage % and drift banner are not perturbed by the CVA write-back.
        if (!result?.readyForSubmission) {
          return;
        }
        // If the operator entered an amount that matches the current rate-card
        // calculation, treat it as "accept the calculation" rather than stamping a
        // redundant manual override onto the record. Mirror the entered amount into
        // brokerageDollar/brokerage so the in-edit read-out stays consistent until
        // the next quote is generated; otherwise the display would revert to the
        // saved amount which may still differ from the calculation.
        const matchesCalculation = this.calculatedBrokerageDollar != null
          && Math.abs(result.brokerageAmount - this.calculatedBrokerageDollar) < 0.01;
        if (matchesCalculation) {
          this.formControlBrokerageManualOverride.setValue(false, { emitEvent: false });
          this.manualBrokerageDollar = undefined;
          this.manualBrokeragePercent = undefined;
          this.brokerageDollar = result.brokerageAmount;
          this.brokerage = result.brokeragePercentage;
        } else {
          this.formControlBrokerageManualOverride.setValue(true, { emitEvent: false });
          this.manualBrokerageDollar = result.brokerageAmount;
          this.manualBrokeragePercent = result.brokeragePercentage;
        }
        this.syncBrokerageDisplay();
      }),
    );
  }

  calculateEstimation(): OverdraftPaymentBreakupDialogData {
    const r = this.buildCalculatedEstimation();

    if (this.isBrokerageManualOverrideActive()) {
      const manualDollar = this.manualBrokerageDollar ?? applicationToBrokerageAmount(this.application) ?? 0;
      const manualPercent = this.manualBrokeragePercent ?? getBrokerage(this.application);
      r.term.brokerageDollar = manualDollar;
      r.term.brokerage = manualPercent;
    }

    this.calculator = r.calculator;
    this.brokerageDollar = r.term.brokerageDollar;
    this.brokerage = r.term.brokerage;
    this.interestRate = r.term.interestRate;
    this.rbaRate = this.rateCard?.rbaRate;
    this.docFee = r.term.docFee;
    this.monthlyFacilityFeePercentage = r.term.monthlyFacilityFeePercentage;
    this.data = this.createApplicationData();
    const rst: OverdraftPaymentBreakupDialogData= {
      term: r.term
    };
    this.rst = rst;
    return rst;
  }

  createApplicationData() {
    const loanTerm: LoanTermValue = this.formControlStep2LoanTerms.value;
    const loanAmount = _.round(this.formControlStep2LoanAmount.value ?? 0, 2);
    const paymentPeriod = 'Monthly';
    const docFeeFinanced = false;
    const docFee = this.docFee;
    const propertyOwner = this.formControlStep2PropertyOwner.value;
    const adverseOnFile = this.formControlStep2AdverseOnFile.value;
    const equifaxDirectorScoreAbove700 = this.formControlStep2EquifaxDirectorScoreAbove700.value;
    const rate = this.interestRate;
    const rbaRate = this.rbaRate;
    const brokerage = this.brokerage;
    const additionalCommission = this.formControlAdditionalCommission.value;
    const margin = (rate == undefined || this.rbaRate == undefined) ? undefined : Math.round((rate - this.rbaRate) * 100) / 100;
    let apiBodyPricingDetails: SaveApplicationPricingDetailsData = {
      LoanTerm: loanTerm?.type ? Number(loanTerm.type) : undefined,
      LoanAmount: loanAmount,
      AmountFinance: loanAmount,
      PaymentPeriod: paymentPeriod,
      DocFee: (docFee != null ? Number(docFee) : undefined),
      DocFeeFinanced: booleanToYesNo(docFeeFinanced),
      PropertyOwner: booleanToYesNo(propertyOwner),
      BankStatementSubmitted: 'No' as const,
      AdverseOnFile: booleanToYesNo(adverseOnFile),
      // ZEN-818 follow-up: OD now writes only the new threshold field; the legacy
      // boolean would mis-classify 600-699 applicants when set from a >=700 toggle.
      EquifaxDirectorScoreThreshold: equifaxDirectorScoreAbove700 ? '>=700' : '<700',
      Rate: rate,
      RbaRate: rbaRate,
      Brokerage: brokerage,
      BrokerageDollar: this.brokerageDollar,
      BrokerageAmount: this.brokerageDollar,
      Margin: margin,
      CreditRateAdjustment: Number(this.formControlCreditRateAdjustment.value),
      MonthlyFacilityFeePercentage: this.monthlyFacilityFeePercentage,
      AdditionalCommission: booleanToYesNo(additionalCommission),
      BrokerageManualOverride: booleanToYesNo(this.formControlBrokerageManualOverride.value),
    }

    const apiBody: Partial<UpdateApplicationData> = {
      UpdateApplicationIgnoreStatus: true,
      PricingDetails: apiBodyPricingDetails as any,
    }
    return apiBody

  }

  save() {
    if (this.data) {
      this.subscriptions.push(this.updateApplicationSfFn(this.application.ApplicationId, this.data as UpdateApplicationData).pipe(
        this.toastService.spinnerObservable(),
        this.toastService.snackBarObservable(`Rate updated`),
        tap(r => {
          if (r.status) {
            this.events.emit({
              type: "business-overdraft-pricing-saved",
              rst: this.rst!,
            });
            this.currentTemplate = this.readModeTemplate;
          }
        })
      ).subscribe());
    } else {
      this.applicationDialogService.openAlertDialog({
        message: 'Error',
        subMessage: `Cannot saved without performing calculation first`,
      });
    }
  }


  onGenerateQuote() {
    // Warn (but do not block) when the facility limit is below the policy minimum.
    // Calculators and submission steppers hard-block; the pricing tab is left
    // soft-gated so existing sub-minimum applications can still be re-priced.
    // Only Credit and Admin users can override and continue below the minimum.
    const facilityLimit = this.formControlStep2LoanAmount.value ?? 0;
    if (facilityLimit > 0 && facilityLimit < MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT) {
      if (this.isNotAdminOrCreditUser) {
        this.applicationDialogService.openAlertDialog({
          message: 'Facility limit below minimum',
          subMessage: `The requested limit is below the $${MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT.toLocaleString()} minimum for Business Overdrafts. Only Credit and Admin users can override this.`,
        });
        return;
      }
      this.subscriptions.push(
        this.applicationDialogService.openConfirmationDialog({
          message: 'Facility limit below minimum',
          subMessage: `The requested limit is below the $${MIN_LOAN_AMOUNT_BUSINESS_LOAN_AND_OVERDRAFT.toLocaleString()} minimum for Business Overdrafts. Continue anyway?`,
        }).afterClosed().subscribe(r => {
          if (r && r.readyForSubmission) {
            this.proceedWithGenerateQuote();
          }
        })
      );
      return;
    }
    this.proceedWithGenerateQuote();
  }

  private proceedWithGenerateQuote() {
    // validation of credit adjustment
    const applicationCreditAdjustment = applicationToCrediRateAdjustment(this.application);
    const currentCreditAdjustment = this.formControlCreditRateAdjustment.value!;
    if (applicationCreditAdjustment != currentCreditAdjustment) {
      if (this.isAdmin) {
        if (currentCreditAdjustment < this.ADMIN_MIN_CREDIT_RATE || currentCreditAdjustment > this.ADMIN_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.ADMIN_MIN_CREDIT_RATE} and ${this.ADMIN_MAX_CREDIT_RATE}`,
          });
          return;
        }
      } else if (this.isCreditUser) {
        if (currentCreditAdjustment < this.CREDIT_MIN_CREDIT_RATE || currentCreditAdjustment > this.CREDIT_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.CREDIT_MIN_CREDIT_RATE} and ${this.CREDIT_MAX_CREDIT_RATE}`,
          });
          return;
        }
      } else {
        if (currentCreditAdjustment < this.NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE || currentCreditAdjustment > this.NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE) {
          this.applicationDialogService.openAlertDialog({
            message: `Credit Adjustment`,
            subMessage: `Credit adjustment needs to be between ${this.NON_CREDIT_OR_ADMIN_MIN_CREDIT_RATE} and ${this.NON_CREDIT_OR_ADMIN_MAX_CREDIT_RATE}`,
          });
          return;
        }
      }
    }

    this.step2EstimatedDrawdownDate = moment().add(1, 'day');
    const subscription = this.applicationDialogService.openBusinessOverdraftComparisonDialog({
      currentValue: this.currentRstValue,
      newValue: this.calculateEstimation(),
      currentAdditionalCommission: applicationToAdditionalCommission(this.application),
      newAdditionalCommission: this.formControlAdditionalCommission.value ?? false,
    }).afterClosed()
      .pipe(
        tap(r => {
          if (r && r.acceptedQuotation) {
            this.docFee = r.docFee;
            this.rst!.term.docFee = r.docFee;
            this.data = this.createApplicationData();
            this.save();
          }
        })
      ).subscribe();
    this.subscriptions.push(subscription);
  }

  canGenerateQuote(): boolean {
    const canGenerate = (
      this.canEditPricingByRole && this.formGroupStep2.valid  &&
      (!this.isApplicationStagePostQa) && (!this.isContractPending)
    );
    if (!canGenerate) {
      this.markSubject.next(true);
    }
    return canGenerate;
  }


  canEditPricing(): boolean {
    return (
      this.canEditPricingByRole &&
      (!this.isApplicationStagePostQa) && (!this.isContractPending)
    );
  }
}
